import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Filter, X } from "lucide-react";
import { cn } from "@/lib/utils";

export type ProductFilterState = {
  searchTerm: string;
  status: string[];
  priceRange: string;
};

type ProductFiltersProps = {
  filters: ProductFilterState;
  onFiltersChange: (filters: ProductFilterState) => void;
  statuses: string[];
  counts?: {
    status: Record<string, number>;
    priceRanges: Record<string, number>;
  };
};

type TabType = "resumo" | "status" | "precos";

export function ProductFilters({
  filters,
  onFiltersChange,
  statuses,
  counts,
}: ProductFiltersProps) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>("resumo");

  const filterCount = 
    filters.status.length +
    (filters.priceRange ? 1 : 0);

  const clearAllFilters = () => {
    onFiltersChange({
      searchTerm: filters.searchTerm,
      status: [],
      priceRange: "",
    });
  };

  const removeFilter = (type: keyof ProductFilterState, value?: string) => {
    if (type === "status") {
      onFiltersChange({
        ...filters,
        status: value ? filters.status.filter((v) => v !== value) : [],
      });
    } else if (type === "priceRange") {
      onFiltersChange({ ...filters, priceRange: "" });
    }
  };

  const tabs: Array<{ id: TabType; label: string; icon: string }> = [
    { id: "resumo", label: "Resumo", icon: "📋" },
    { id: "status", label: "Status", icon: "📊" },
    { id: "precos", label: "Preços", icon: "💰" },
  ];

  const getTabCount = (tabId: TabType) => {
    switch (tabId) {
      case "status": return filters.status.length;
      case "precos": return filters.priceRange ? 1 : 0;
      default: return 0;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button variant="outline" className="relative">
                <Filter className="h-4 w-4 mr-2" />
                Filtros
                {filterCount > 0 && (
                  <Badge className="ml-2 h-5 w-5 p-0 flex items-center justify-center" variant="default">
                    {filterCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="top">
            Filtre a listagem de produtos
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent className="w-[500px] p-0" align="start">
        <div className="flex h-[400px]">
          {/* Sidebar */}
          <div className="w-48 border-r bg-muted/30 p-2 overflow-y-auto">
            {tabs.map((tab) => {
              const count = getTabCount(tab.id);
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-2 rounded text-sm mb-1",
                    activeTab === tab.id
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  )}
                >
                  <span className="flex items-center gap-2">
                    <span>{tab.icon}</span>
                    <span>{tab.label}</span>
                  </span>
                  {count > 0 && (
                    <Badge variant={activeTab === tab.id ? "secondary" : "default"} className="h-5 w-5 p-0 flex items-center justify-center text-xs">
                      {count}
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="flex-1 p-4 overflow-y-auto">
            {activeTab === "resumo" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Resumo dos Filtros</h3>
                  {filterCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={clearAllFilters}>
                      Limpar tudo
                    </Button>
                  )}
                </div>

                {filterCount === 0 ? (
                  <p className="text-muted-foreground text-sm">Nenhum filtro aplicado</p>
                ) : (
                  <div className="space-y-3">
                    {filters.status.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Status</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.status.map((s) => (
                            <Badge key={s} variant="secondary" className="gap-1 capitalize">
                              {s}
                              <X
                                className="h-3 w-3 cursor-pointer"
                                onClick={() => removeFilter("status", s)}
                              />
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {filters.priceRange && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Faixa de Preço</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <Badge variant="secondary" className="gap-1">
                            {filters.priceRange === "0-100" && "Até R$ 100"}
                            {filters.priceRange === "100-500" && "R$ 100 - R$ 500"}
                            {filters.priceRange === "500-1000" && "R$ 500 - R$ 1.000"}
                            {filters.priceRange === "1000-5000" && "R$ 1.000 - R$ 5.000"}
                            {filters.priceRange === "5000+" && "Acima de R$ 5.000"}
                            <X
                              className="h-3 w-3 cursor-pointer"
                              onClick={() => removeFilter("priceRange")}
                            />
                          </Badge>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === "status" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Status</h3>
                <div className="space-y-2">
                  {statuses.map((status) => (
                    <div key={status} className="flex items-center space-x-2">
                      <Checkbox
                        id={`status-${status}`}
                        checked={filters.status.includes(status)}
                        onCheckedChange={(checked) => {
                          onFiltersChange({
                            ...filters,
                            status: checked
                              ? [...filters.status, status]
                              : filters.status.filter((s) => s !== status),
                          });
                        }}
                      />
                      <Label htmlFor={`status-${status}`} className="capitalize flex-1 flex items-center gap-2">
                        {status}
                        <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                          {counts?.status?.[status] ?? 0}
                        </Badge>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "precos" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Faixa de Preço</h3>
                <RadioGroup value={filters.priceRange} onValueChange={(value) => onFiltersChange({ ...filters, priceRange: value })}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="0-100" id="price1" />
                    <Label htmlFor="price1" className="flex-1 flex items-center gap-2">
                      Até R$ 100
                      <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.priceRanges?.["0-100"] ?? 0}</Badge>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="100-500" id="price2" />
                    <Label htmlFor="price2" className="flex-1 flex items-center gap-2">
                      R$ 100 - R$ 500
                      <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.priceRanges?.["100-500"] ?? 0}</Badge>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="500-1000" id="price3" />
                    <Label htmlFor="price3" className="flex-1 flex items-center gap-2">
                      R$ 500 - R$ 1.000
                      <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.priceRanges?.["500-1000"] ?? 0}</Badge>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="1000-5000" id="price4" />
                    <Label htmlFor="price4" className="flex-1 flex items-center gap-2">
                      R$ 1.000 - R$ 5.000
                      <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.priceRanges?.["1000-5000"] ?? 0}</Badge>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="5000+" id="price5" />
                    <Label htmlFor="price5" className="flex-1 flex items-center gap-2">
                      Acima de R$ 5.000
                      <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.priceRanges?.["5000+"] ?? 0}</Badge>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

